/**
 * Data Adapters for ScopeOps Platform
 * Normalizes API responses to ensure UI components receive guaranteed shapes
 * Handles null/undefined values with safe defaults
 */

// ========================================
// DEFAULT VALUES
// ========================================

const DEFAULTS = {
  string: '',
  number: 0,
  percent: 0,
  currency: 'ZAR',
  date: null,
  array: [],
  object: {},
  status: 'Unknown',
  boolean: false,
};

// ========================================
// TYPE UTILITIES
// ========================================

/**
 * Safely convert to number, returns default if invalid
 */
export const toNumber = (value, defaultVal = 0) => {
  if (value === null || value === undefined) return defaultVal;
  const num = Number(value);
  return isNaN(num) ? defaultVal : num;
};

/**
 * Safely convert to string, trims whitespace
 */
export const toString = (value, defaultVal = '') => {
  if (value === null || value === undefined) return defaultVal;
  return String(value).trim();
};

/**
 * Safely convert to array
 */
export const toArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [value];
};

/**
 * Safely convert to boolean
 */
export const toBool = (value, defaultVal = false) => {
  if (value === null || value === undefined) return defaultVal;
  return Boolean(value);
};

/**
 * Safely access nested property
 */
export const get = (obj, path, defaultVal = undefined) => {
  if (!obj || !path) return defaultVal;
  const keys = path.split('.');
  let result = obj;
  for (const key of keys) {
    result = result?.[key];
    if (result === undefined) return defaultVal;
  }
  return result ?? defaultVal;
};

/**
 * Clamp a number between min and max
 */
export const clamp = (value, min, max) => {
  const num = toNumber(value, min);
  return Math.min(Math.max(num, min), max);
};

/**
 * Format currency safely
 */
export const formatCurrency = (value, symbol = '$') => {
  const num = toNumber(value, 0);
  if (Math.abs(num) >= 1e6) return `${symbol}${(num / 1e6).toFixed(1)}M`;
  if (Math.abs(num) >= 1e3) return `${symbol}${(num / 1e3).toFixed(1)}K`;
  return `${symbol}${num.toFixed(0)}`;
};

/**
 * Safely format date
 */
export const formatDate = (value, options = {}) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleDateString(undefined, options);
  } catch {
    return '-';
  }
};

// ========================================
// ENTITY ADAPTERS
// ========================================

/**
 * Normalize a Feature entity
 */
export const normalizeFeature = (feature) => {
  if (!feature) return null;
  
  return {
    id: toString(feature.id || feature._id),
    name: toString(feature.name, 'Unnamed Feature'),
    description: toString(feature.description),
    status: toString(feature.status, 'Unknown'),
    percent_complete: clamp(feature.percent_complete, 0, 100),
    
    // Parent references
    epic_id: toString(feature.epic_id),
    epic_name: toString(feature.epic_name),
    project_id: toString(feature.project_id),
    project_name: toString(feature.project_name),
    program_name: toString(feature.program_name),
    portfolio_name: toString(feature.portfolio_name),
    
    // Dates
    start_date: feature.start_date || null,
    due_date: feature.due_date || null,
    revised_date: feature.revised_date || feature.revised_due_date || null,
    
    // Cost metrics
    est_cost: toNumber(feature.est_cost || feature.estimated_cost),
    act_cost: toNumber(feature.act_cost || feature.actual_cost),
    variance: toNumber(feature.variance || feature.cost_variance),
    
    // Counts
    blocked_count: toNumber(feature.blocked_count),
    stories_count: toNumber(feature.stories_count || feature.work_item_count),
    
    // Flags
    _is_matched: toBool(feature._is_matched),
    _is_parent: toBool(feature._is_parent),
    
    // Pass through original for backward compat
    _original: feature,
  };
};

/**
 * Normalize an Epic entity
 */
export const normalizeEpic = (epic) => {
  if (!epic) return null;
  
  return {
    id: toString(epic.id || epic._id),
    name: toString(epic.name, 'Unnamed Epic'),
    description: toString(epic.description),
    status: toString(epic.status, 'Unknown'),
    percent_complete: clamp(epic.percent_complete, 0, 100),
    
    // Parent references
    project_id: toString(epic.project_id),
    project_name: toString(epic.project_name),
    program_id: toString(epic.program_id),
    program_name: toString(epic.program_name),
    portfolio_name: toString(epic.portfolio_name),
    
    // Dates
    start_date: epic.start_date || null,
    due_date: epic.due_date || null,
    revised_due_date: epic.revised_due_date || null,
    
    // Cost metrics
    est_cost: toNumber(epic.est_cost),
    act_cost: toNumber(epic.act_cost),
    variance: toNumber(epic.variance),
    
    // Counts
    features_count: toNumber(epic.features_count || epic.feature_count),
    blocked_count: toNumber(epic.blocked_count),
    
    // Owner
    owner: toString(epic.owner || epic.epic_owner),
    
    _original: epic,
  };
};

/**
 * Normalize a Project entity
 */
export const normalizeProject = (project) => {
  if (!project) return null;
  
  return {
    id: toString(project.id || project._id),
    name: toString(project.name, 'Unnamed Project'),
    description: toString(project.description),
    status: toString(project.status, 'Unknown'),
    percent_complete: clamp(project.percent_complete, 0, 100),
    
    // Parent references
    program_id: toString(project.program_id),
    program_name: toString(project.program_name),
    portfolio_id: toString(project.portfolio_id),
    portfolio_name: toString(project.portfolio_name),
    
    // Dates
    start_date: project.start_date || null,
    due_date: project.due_date || null,
    
    // Cost metrics
    est_cost: toNumber(project.est_cost || project.estimated_cost),
    act_cost: toNumber(project.act_cost || project.actual_cost),
    variance: toNumber(project.variance),
    budget: toNumber(project.budget),
    
    // Counts
    epics_count: toNumber(project.epics_count),
    risks_count: toNumber(project.risks_count || project.open_risks),
    issues_count: toNumber(project.issues_count || project.open_issues),
    
    // Health metrics
    health_score: clamp(project.health_score, 0, 100),
    delivery_confidence: clamp(project.delivery_confidence, 0, 100),
    
    // Owner
    project_manager: toString(project.project_manager),
    
    _original: project,
  };
};

/**
 * Normalize a Risk entity
 */
export const normalizeRisk = (risk) => {
  if (!risk) return null;
  
  return {
    id: toString(risk.id || risk._id),
    title: toString(risk.title || risk.name, 'Unnamed Risk'),
    description: toString(risk.description),
    status: toString(risk.status, 'Open'),
    severity: toString(risk.severity, 'Medium'),
    likelihood: toString(risk.likelihood, 'Medium'),
    impact: toString(risk.impact, 'Medium'),
    
    // References
    project_id: toString(risk.project_id),
    project_name: toString(risk.project_name),
    epic_id: toString(risk.epic_id),
    feature_id: toString(risk.feature_id),
    
    // Dates
    identified_date: risk.identified_date || risk.created_at || null,
    due_date: risk.due_date || risk.target_date || null,
    
    // Mitigation
    mitigation: toString(risk.mitigation || risk.mitigation_plan),
    owner: toString(risk.owner || risk.assigned_to),
    
    _original: risk,
  };
};

/**
 * Normalize an Issue entity
 */
export const normalizeIssue = (issue) => {
  if (!issue) return null;
  
  return {
    id: toString(issue.id || issue._id),
    title: toString(issue.title || issue.name, 'Unnamed Issue'),
    description: toString(issue.description),
    status: toString(issue.status, 'Open'),
    priority: toString(issue.priority, 'Medium'),
    severity: toString(issue.severity, 'Medium'),
    
    // References
    project_id: toString(issue.project_id),
    project_name: toString(issue.project_name),
    
    // Dates
    created_at: issue.created_at || null,
    due_date: issue.due_date || null,
    resolved_at: issue.resolved_at || null,
    
    // Owner
    owner: toString(issue.owner || issue.assigned_to),
    reporter: toString(issue.reporter || issue.created_by),
    
    _original: issue,
  };
};

/**
 * Normalize Cost Metrics object
 */
export const normalizeCostMetrics = (metrics) => {
  if (!metrics) return { est_cost: 0, act_cost: 0, variance: 0, currency: 'ZAR' };
  
  return {
    est_cost: toNumber(metrics.est_cost || metrics.estimated_cost || metrics.estimated),
    act_cost: toNumber(metrics.act_cost || metrics.actual_cost || metrics.actual),
    variance: toNumber(metrics.variance || metrics.cost_variance),
    currency: toString(metrics.currency, 'ZAR'),
    budget: toNumber(metrics.budget),
    forecast: toNumber(metrics.forecast),
  };
};

/**
 * Normalize Dashboard KPIs
 */
export const normalizeKPIs = (kpis) => {
  if (!kpis) return {};
  
  return {
    total: toNumber(kpis.total),
    completed: toNumber(kpis.completed),
    in_progress: toNumber(kpis.in_progress || kpis.inProgress),
    blocked: toNumber(kpis.blocked),
    behind: toNumber(kpis.behind),
    at_risk: toNumber(kpis.at_risk || kpis.atRisk),
    
    percent_complete: clamp(kpis.percent_complete || kpis.avgComplete, 0, 100),
    health_score: clamp(kpis.health_score, 0, 100),
    confidence: clamp(kpis.confidence || kpis.delivery_confidence, 0, 100),
    
    est_cost: toNumber(kpis.est_cost || kpis.totalEstCost),
    act_cost: toNumber(kpis.act_cost || kpis.totalActCost),
    variance: toNumber(kpis.variance || kpis.totalVariance),
  };
};

/**
 * Normalize a list of entities with a normalizer function
 */
export const normalizeList = (list, normalizer) => {
  if (!list || !Array.isArray(list)) return [];
  return list.filter(Boolean).map(normalizer).filter(Boolean);
};

// ========================================
// STATUS HELPERS
// ========================================

const VALID_STATUSES = [
  'Blocked', 'Behind', 'Due Date at Risk', 'At Risk',
  'In Progress', 'In Review', 'Not Started', 'Completed', 
  'Not Required', 'On Track', 'Open', 'Closed', 'Resolved'
];

/**
 * Validate and normalize a status string
 */
export const normalizeStatus = (status, defaultVal = 'Unknown') => {
  if (!status) return defaultVal;
  const normalized = toString(status);
  
  // Try exact match
  if (VALID_STATUSES.includes(normalized)) return normalized;
  
  // Try case-insensitive match
  const found = VALID_STATUSES.find(s => s.toLowerCase() === normalized.toLowerCase());
  if (found) return found;
  
  return defaultVal;
};

/**
 * Get status category for grouping
 */
export const getStatusCategory = (status) => {
  const normalized = normalizeStatus(status);
  if (['Blocked', 'Behind', 'Due Date at Risk', 'At Risk'].includes(normalized)) return 'at-risk';
  if (['In Progress', 'In Review'].includes(normalized)) return 'active';
  if (['Completed', 'Resolved', 'Closed'].includes(normalized)) return 'done';
  return 'pending';
};

// ========================================
// SAFE SELECTORS
// ========================================

/**
 * Select feature status safely
 */
export const selectFeatureStatus = (feature) => normalizeStatus(feature?.status);

/**
 * Select cost variance safely  
 */
export const selectVariance = (entity) => toNumber(entity?.variance || entity?.cost_variance);

/**
 * Select percent complete safely
 */
export const selectPercentComplete = (entity) => clamp(entity?.percent_complete, 0, 100);

/**
 * Check if entity has blockers
 */
export const hasBlockers = (entity) => toNumber(entity?.blocked_count) > 0;

// ========================================
// API RESPONSE ADAPTERS
// ========================================

/**
 * Adapt a features API response
 */
export const adaptFeaturesResponse = (response) => {
  const data = response?.data || response || {};
  return {
    features: normalizeList(data.features || data, normalizeFeature),
    total: toNumber(data.total || data.features?.length),
    page: toNumber(data.page, 1),
    limit: toNumber(data.limit, 50),
  };
};

/**
 * Adapt an epics API response
 */
export const adaptEpicsResponse = (response) => {
  const data = response?.data || response || {};
  return {
    epics: normalizeList(data.epics || data, normalizeEpic),
    total: toNumber(data.total || data.epics?.length),
  };
};

/**
 * Adapt a risks API response
 */
export const adaptRisksResponse = (response) => {
  const data = response?.data || response || {};
  return {
    risks: normalizeList(data.risks || data, normalizeRisk),
    total: toNumber(data.total || data.risks?.length),
  };
};

/**
 * Adapt a dashboard API response
 */
export const adaptDashboardResponse = (response) => {
  const data = response?.data || response || {};
  return {
    kpis: normalizeKPIs(data.kpis || data.metrics || data),
    items: normalizeList(data.items || data.projects || data.epics || []),
    costs: normalizeCostMetrics(data.costs || data.cost_metrics),
  };
};

// ========================================
// REQUIREMENT NORMALIZERS
// ========================================

/**
 * Normalize a quality flags object
 */
export const normalizeQualityFlags = (flags) => ({
  missing_ac: toBool(flags?.missing_ac),
  missing_category: toBool(flags?.missing_category),
  missing_dependencies: toBool(flags?.missing_dependencies),
  ambiguous_language: toBool(flags?.ambiguous_language),
  incomplete_fields: toBool(flags?.incomplete_fields),
  score: clamp(flags?.score ?? 100, 0, 100),
});

/**
 * Normalize acceptance criterion
 */
export const normalizeAcceptanceCriterion = (ac) => ({
  id: toString(ac?.id),
  template_id: ac?.template_id || null,
  content: toString(ac?.content),
  type: toString(ac?.type, 'scenario'),
  given: ac?.given || null,
  when: ac?.when || null,
  then: ac?.then || null,
  is_met: toBool(ac?.is_met),
});

/**
 * Normalize requirement risk
 */
export const normalizeRequirementRisk = (risk) => ({
  id: toString(risk?.id),
  template_id: risk?.template_id || null,
  description: toString(risk?.description),
  category: toString(risk?.category, 'Delivery'),
  likelihood: toString(risk?.likelihood, 'Medium'),
  impact: toString(risk?.impact, 'Medium'),
  mitigation: risk?.mitigation || null,
  owner: risk?.owner || null,
});

/**
 * Normalize requirement constraint
 */
export const normalizeRequirementConstraint = (constraint) => ({
  id: toString(constraint?.id),
  template_id: constraint?.template_id || null,
  description: toString(constraint?.description),
  category: toString(constraint?.category, 'Delivery'),
  reason: constraint?.reason || null,
  impact_if_violated: constraint?.impact_if_violated || null,
});

/**
 * Normalize requirement dependency
 */
export const normalizeRequirementDependency = (dep) => ({
  id: toString(dep?.id),
  template_id: dep?.template_id || null,
  description: toString(dep?.description),
  type: toString(dep?.type, 'Internal'),
  dependent_on: dep?.dependent_on || null,
  expected_date: dep?.expected_date || null,
  status: toString(dep?.status, 'Open'),
});

/**
 * Normalize a requirement object
 */
export const normalizeRequirement = (req) => ({
  id: toString(req?.id),
  tenant_id: req?.tenant_id || null,
  project_id: req?.project_id || null,
  parent_id: req?.parent_id || null,
  parent_theme_id: req?.parent_theme_id || null,
  name: toString(req?.name),
  description: toString(req?.description),
  hierarchy_type: toString(req?.hierarchy_type, 'requirement'),
  category: req?.category || null,
  template_id: req?.template_id || null,
  template_data: req?.template_data || {},
  acceptance_criteria: toArray(req?.acceptance_criteria).map(normalizeAcceptanceCriterion),
  risks: toArray(req?.risks).map(normalizeRequirementRisk),
  constraints: toArray(req?.constraints).map(normalizeRequirementConstraint),
  dependencies: toArray(req?.dependencies).map(normalizeRequirementDependency),
  priority: req?.priority || null,
  owner: req?.owner || null,
  estimated_effort: toNumber(req?.estimated_effort, null),
  labels: toArray(req?.labels),
  order: toNumber(req?.order),
  status: toString(req?.status, 'draft'),
  quality_flags: normalizeQualityFlags(req?.quality_flags),
  baseline_id: req?.baseline_id || null,
  wbs_node_id: req?.wbs_node_id || null,
  children: toArray(req?.children).map(normalizeRequirement),
  created_at: req?.created_at || null,
  updated_at: req?.updated_at || null,
});

/**
 * Normalize a WBS node
 */
export const normalizeWBSNode = (node) => ({
  id: toString(node?.id),
  tenant_id: node?.tenant_id || null,
  project_id: node?.project_id || null,
  baseline_id: node?.baseline_id || null,
  parent_id: node?.parent_id || null,
  requirement_id: node?.requirement_id || null,
  template_id: node?.template_id || null,
  name: toString(node?.name),
  description: toString(node?.description),
  node_type: toString(node?.node_type, 'work_package'),
  category: node?.category || null,
  estimated_hours: toNumber(node?.estimated_hours, null),
  estimated_duration: node?.estimated_duration || null,
  dependencies: toArray(node?.dependencies),
  order: toNumber(node?.order),
  children: toArray(node?.children).map(normalizeWBSNode),
  created_at: node?.created_at || null,
  updated_at: node?.updated_at || null,
});

/**
 * Normalize a baseline
 */
export const normalizeBaseline = (baseline) => ({
  id: toString(baseline?.id),
  tenant_id: baseline?.tenant_id || null,
  project_id: baseline?.project_id || null,
  version: toNumber(baseline?.version, 1),
  name: toString(baseline?.name),
  notes: baseline?.notes || null,
  status: toString(baseline?.status, 'draft'),
  requirement_count: toNumber(baseline?.requirement_count),
  wbs_node_count: toNumber(baseline?.wbs_node_count),
  quality_score: toNumber(baseline?.quality_score),
  created_by: baseline?.created_by || null,
  created_at: baseline?.created_at || null,
  updated_at: baseline?.updated_at || null,
  submitted_by: baseline?.submitted_by || null,
  submitted_at: baseline?.submitted_at || null,
  approved_by: baseline?.approved_by || null,
  approved_at: baseline?.approved_at || null,
  approval_notes: baseline?.approval_notes || null,
  rejected_by: baseline?.rejected_by || null,
  rejected_at: baseline?.rejected_at || null,
  rejection_reason: baseline?.rejection_reason || null,
});

/**
 * Adapt requirements response
 */
export const adaptRequirementsResponse = (response) => {
  const data = response?.data || response || {};
  return {
    requirements: normalizeList(data.requirements || [], normalizeRequirement),
    total: toNumber(data.total || data.requirements?.length),
    is_tree: toBool(data.is_tree),
  };
};

/**
 * Adapt baselines response
 */
export const adaptBaselinesResponse = (response) => {
  const data = response?.data || response || {};
  return {
    baselines: normalizeList(data.baselines || [], normalizeBaseline),
    total: toNumber(data.total || data.baselines?.length),
  };
};

/**
 * Adapt WBS nodes response
 */
export const adaptWBSResponse = (response) => {
  const data = response?.data || response || {};
  return {
    nodes: normalizeList(data.nodes || [], normalizeWBSNode),
    total: toNumber(data.total || data.nodes?.length),
    is_tree: toBool(data.is_tree),
  };
};

export default {
  // Types
  toNumber,
  toString,
  toArray,
  toBool,
  get,
  clamp,
  formatCurrency,
  formatDate,
  
  // Entity normalizers
  normalizeFeature,
  normalizeEpic,
  normalizeProject,
  normalizeRisk,
  normalizeIssue,
  normalizeCostMetrics,
  normalizeKPIs,
  normalizeList,
  normalizeStatus,
  normalizeRequirement,
  normalizeBaseline,
  normalizeWBSNode,
  normalizeQualityFlags,
  
  // Selectors
  selectFeatureStatus,
  selectVariance,
  selectPercentComplete,
  hasBlockers,
  getStatusCategory,
  
  // Response adapters
  adaptFeaturesResponse,
  adaptEpicsResponse,
  adaptRisksResponse,
  adaptDashboardResponse,
  adaptRequirementsResponse,
  adaptBaselinesResponse,
  adaptWBSResponse,
};
